import api from './api';

class SupportService {
  // ── FAQ ──

  async getFaq(params = {}) {
    const response = await api.get('/support/faq', { params });
    return response.data;
  }

  async getFaqArticle(idOrSlug) {
    const response = await api.get(`/support/faq/${idOrSlug}`);
    return response.data;
  }

  async voteFaqArticle(id, vote) {
    const response = await api.post(`/support/faq/${id}/vote`, { vote });
    return response.data;
  }

  // ── Agent Status ──

  async getAgentStatus() {
    const response = await api.get('/support/agent-status');
    return response.data;
  }

  // ── Tickets (authenticated) ──

  async getMyTickets(params = {}) {
    const response = await api.get('/support/tickets', { params });
    return response.data;
  }

  async getTicket(id) {
    const response = await api.get(`/support/tickets/${id}`);
    return response.data;
  }

  async createTicket(formData) {
    const response = await api.post('/support/tickets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async addMessage(ticketId, formData) {
    const response = await api.post(`/support/tickets/${ticketId}/messages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async reopenTicket(ticketId) {
    const response = await api.patch(`/support/tickets/${ticketId}/reopen`);
    return response.data;
  }

  async markRead(ticketId) {
    const response = await api.post(`/support/tickets/${ticketId}/read`);
    return response.data;
  }

  async getUnreadCount() {
    const response = await api.get('/support/unread-count');
    return response.data;
  }

  async checkUserIsAgent() {
    const response = await api.get('/support/user/is-agent');
    return response.data;
  }

  async getAgentTickets(params = {}) {
    const response = await api.get('/support/agent/tickets', { params });
    return response.data;
  }

  async getAgentTicket(id) {
    const response = await api.get(`/support/agent/tickets/${id}`);
    return response.data;
  }

  async agentReply(id, formData) {
    const response = await api.post(`/support/agent/tickets/${id}/reply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async agentUpdateStatus(id, status) {
    const response = await api.patch(`/support/agent/tickets/${id}/status`, { status });
    return response.data;
  }

  async toggleAgentPause() {
    const response = await api.patch('/support/agent/pause');
    return response.data;
  }

  // ── Anonymous ticket by token ──

  async getTicketByToken(token) {
    const response = await api.get(`/support/tickets/token/${token}`);
    return response.data;
  }

  async addMessageByToken(ticketId, token, formData) {
    const response = await api.post(`/support/tickets/${ticketId}/messages/token/${token}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // ── Admin ──

  async getAdminDashboard() {
    const response = await api.get('/admin/support/dashboard');
    return response.data;
  }

  async getAdminTickets(params = {}) {
    const response = await api.get('/admin/support/tickets', { params });
    return response.data;
  }

  async getAdminTicket(id) {
    const response = await api.get(`/admin/support/tickets/${id}`);
    return response.data;
  }

  async updateTicketStatus(id, status) {
    const response = await api.patch(`/admin/support/tickets/${id}/status`, { status });
    return response.data;
  }

  async updateTicketPriority(id, priority) {
    const response = await api.patch(`/admin/support/tickets/${id}/priority`, { priority });
    return response.data;
  }

  async assignAgent(ticketId, agentId) {
    const response = await api.patch(`/admin/support/tickets/${ticketId}/assign`, { agentId });
    return response.data;
  }

  async addAdminReply(ticketId, formData) {
    const response = await api.post(`/admin/support/tickets/${ticketId}/reply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // ── Admin Agents ──

  async getAgents() {
    const response = await api.get('/admin/support/agents');
    return response.data;
  }

  async searchUsers(query) {
    const response = await api.get('/admin/support/users/search', { params: { query } });
    return response.data;
  }

  async createAgent(data) {
    const response = await api.post('/admin/support/agents', data);
    return response.data;
  }

  async updateAgent(id, data) {
    const response = await api.put(`/admin/support/agents/${id}`, data);
    return response.data;
  }

  async toggleAgentPause(id) {
    const response = await api.patch(`/admin/support/agents/${id}/pause`);
    return response.data;
  }

  async removeAgent(id) {
    const response = await api.delete(`/admin/support/agents/${id}`);
    return response.data;
  }

  // ── Admin FAQ ──

  async getAdminFaqCategories() {
    const response = await api.get('/admin/support/faq/categories');
    return response.data;
  }

  async createFaqCategory(data) {
    const response = await api.post('/admin/support/faq/categories', data);
    return response.data;
  }

  async updateFaqCategory(id, data) {
    const response = await api.put(`/admin/support/faq/categories/${id}`, data);
    return response.data;
  }

  async deleteFaqCategory(id) {
    const response = await api.delete(`/admin/support/faq/categories/${id}`);
    return response.data;
  }

  async getAdminFaqArticles(params = {}) {
    const response = await api.get('/admin/support/faq/articles', { params });
    return response.data;
  }

  async createFaqArticle(data) {
    const response = await api.post('/admin/support/faq/articles', data);
    return response.data;
  }

  async updateFaqArticle(id, data) {
    const response = await api.put(`/admin/support/faq/articles/${id}`, data);
    return response.data;
  }

  async deleteFaqArticle(id) {
    const response = await api.delete(`/admin/support/faq/articles/${id}`);
    return response.data;
  }

  async reorderFaqArticles(orderedIds) {
    const response = await api.post('/admin/support/faq/articles/reorder', { orderedIds });
    return response.data;
  }

  async reorderFaqCategories(orderedIds) {
    const response = await api.post('/admin/support/faq/categories/reorder', { orderedIds });
    return response.data;
  }

  async getFaqStats() {
    const response = await api.get('/admin/support/faq/stats');
    return response.data;
  }

  // ── Admin Settings ──

  async getSettings() {
    const response = await api.get('/admin/support/settings');
    return response.data;
  }

  async updateSettings(data) {
    const response = await api.put('/admin/support/settings', data);
    return response.data;
  }

  // ── Admin Canned Responses ──

  async getCannedResponses() {
    const response = await api.get('/admin/support/canned-responses');
    return response.data;
  }

  async createCannedResponse(data) {
    const response = await api.post('/admin/support/canned-responses', data);
    return response.data;
  }

  async updateCannedResponse(id, data) {
    const response = await api.put(`/admin/support/canned-responses/${id}`, data);
    return response.data;
  }

  async deleteCannedResponse(id) {
    const response = await api.delete(`/admin/support/canned-responses/${id}`);
    return response.data;
  }
}

export default new SupportService();
